import * as React from "react";
import { CandyTooltip as Tooltip, Gapped, Input } from "react-ui";

import {
    UNIT_MENU_INPUT_ENTER,
    UNIT_MENU_NEXT_LINK_CLICK,
    UNIT_MENU_PREVIOUS_LINK_CLICK,
} from "../../../../../metricsAction";
import { UnitId, unitIdToLegacyUnitId, unitIdWithInstance } from "../../../../Unit/UnitId/UnitId";
import { isEnterKey } from "../../../../../../Helpers/KeyboardUtils";
import { useMetrics } from "../../../../../Internals/Metrics/MetricsProviderContext";

import { StepLink, StepLinks } from "./MultipleUnitPaging.styles";

interface MultipleUnitPagingProps {
    id: UnitId;
    className?: undefined | string;
    currentInstance: undefined | string;
    instances: readonly string[];
    onGoToUnit: (id: UnitId) => void;
}

enum TooltipTriggerType {
    Closed = "closed",
    Opened = "opened",
}

export function MultipleUnitPaging(props: MultipleUnitPagingProps): JSX.Element {
    const { currentInstance, instances } = props;
    const track = useMetrics();
    const [inputValue, setInputValue] = React.useState("");
    const [tooltipTrigger, setTooltipTrigger] = React.useState(TooltipTriggerType.Closed);

    const legacyUnitId = React.useMemo(() => unitIdToLegacyUnitId(props.id), [props.id]);
    const currentInstanceIndex = React.useMemo(() => {
        const resultIndex = props.instances.findIndex(x => x === currentInstance);
        if (resultIndex === -1) {
            return undefined;
        }
        return resultIndex;
    }, [currentInstance]);

    React.useEffect(() => {
        setInputValue(currentInstanceIndex == undefined ? "" : (currentInstanceIndex + 1).toString());
    }, [currentInstanceIndex]);

    const renderTooltip = () => <div data-tid="DescriptionItemText">{`${inputValue} экземпляр пока не добавлен`}</div>;

    const handleInputKeyUp = (event: React.KeyboardEvent): void => {
        if (isEnterKey(event)) {
            handleKeyEnter();
        }
    };

    const handleInputChange = (nextValue: string): void => {
        const numberValue = nextValue.replace(/[^0-9]/g, "");
        setInputValue(numberValue);
        setTooltipTrigger(TooltipTriggerType.Closed);
    };

    const handleCloseTooltip = () => {
        setTooltipTrigger(TooltipTriggerType.Closed);
    };

    const handleKeyEnter = (): void => {
        track(UNIT_MENU_INPUT_ENTER, { unit: legacyUnitId, inputValue: inputValue });
        const numberValue = Number(inputValue);

        if (Number.isNaN(numberValue)) {
            return;
        }

        if (numberValue > props.instances.length) {
            setTooltipTrigger(TooltipTriggerType.Opened);
        } else {
            const instance = props.instances[numberValue - 1];
            if (instance != undefined) {
                props.onGoToUnit(unitIdWithInstance(props.id, instance));
            }
        }
    };

    const handlePreviousLinkClick = (): void => {
        track(UNIT_MENU_PREVIOUS_LINK_CLICK, { unit: legacyUnitId });
        if (currentInstanceIndex == undefined) {
            return;
        }
        const prevInstance = props.instances[currentInstanceIndex - 1];
        if (prevInstance != undefined) {
            props.onGoToUnit(unitIdWithInstance(props.id, prevInstance));
        }
    };

    const handleNextLinkClick = (): void => {
        track(UNIT_MENU_NEXT_LINK_CLICK, { unit: legacyUnitId });
        if (currentInstanceIndex == undefined) {
            return;
        }
        const nextInstance = props.instances[currentInstanceIndex + 1];
        if (nextInstance != undefined) {
            props.onGoToUnit(unitIdWithInstance(props.id, nextInstance));
        }
    };

    return (
        <>
            <StepLinks className={props.className}>
                <StepLink
                    previous
                    disabled={currentInstance === instances[0]}
                    onClick={handlePreviousLinkClick}
                    data-tid="PreviousInstanceLink">
                    ← Предыдущий
                </StepLink>
                <StepLink
                    disabled={currentInstance === instances[instances.length - 1]}
                    onClick={handleNextLinkClick}
                    data-tid="NextInstanceLink">
                    Следующий →
                </StepLink>
            </StepLinks>
            <Tooltip
                pos="right middle"
                render={renderTooltip}
                trigger={tooltipTrigger}
                onCloseClick={handleCloseTooltip}
                width={130}>
                <Gapped gap={8}>
                    <div>Экземпляр</div>

                    <Input
                        align="center"
                        width={45}
                        value={inputValue}
                        onValueChange={handleInputChange}
                        onKeyUp={handleInputKeyUp}
                        data-tid="InstanceNumber"
                    />

                    <span>{"из"}</span>
                    <span>{`${props.instances.length}`}</span>
                </Gapped>
            </Tooltip>
        </>
    );
}
